import { api } from "../api";
import { openModal, closeModal, modalBody } from "../components/modal";
import { confirmDialog } from "../components/confirm-dialog";
import { openCsvImportModal } from "../components/csv-import-modal";
import { showToast } from "../utils/toast";
import { Icons } from "../utils/icons";
import type { CurriculumTemplate, DepartmentGroup } from "../types";
import { esc } from "../utils/html";
import { overNote } from "../components/lehrplan-editor";

/**
 * Lehrplan-Vorlagen der Plattform, analog zu den Fachbereichsgruppen.
 *
 * Eine Vorlage bildet die Stundentafel einer Verordnung ab: Fächergruppen mit ihrer
 * Pflicht, Fächer mit ihrer Pflicht (ohne Gruppe) und einem Vorschlag für den Wert der
 * Schule. Schulen übernehmen sie in ihren Lehrplan und gestalten ihn ab dann selbst;
 * spätere Änderungen hier wirken nicht auf Schulen zurück.
 *
 * Gleiche Darstellung wie der Lehrplan der Schule: die Stundentafel als Tabelle, Zeilen
 * öffnen sich per Klick in einem Fenster. Gespeichert wird der Inhalt am Stück
 * (PUT …/inhalt), weil Gruppen und Fächer über den Namen aufeinander verweisen.
 */

/** color: gemeinsame Farbe aller Fächer der Gruppe, null = jedes Fach behält seine. */
interface DraftGroup { name: string; pflichtUe: number | null; color: string | null }
interface DraftSubject {
  name: string; shortName: string | null; color: string; group: string | null;
  pflichtUe: number | null; schuleUe: number | null;
}

let templates: CurriculumTemplate[] = [];
let groupsCatalog: DepartmentGroup[] = [];
let selectedId: number | null = null;
let groups: DraftGroup[] = [];
let subjects: DraftSubject[] = [];
let currentEl: HTMLElement | null = null;

// ── Hilfen ──

function fmt(n: number | null | undefined): string {
  if (n === null || n === undefined) return "";
  return (Math.round(n * 100) / 100).toLocaleString("de-DE");
}

function parseUe(raw: string): number | null | "invalid" {
  const v = raw.trim().replace(/\./g, "").replace(",", ".");
  if (v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : "invalid";
}

function ueInput(id: string, value: number | null | undefined, attrs = ""): string {
  return `<div class="lp-unit"><input type="text" inputmode="decimal" id="${id}" value="${esc(fmt(value))}" ${attrs} /><span>UE</span></div>`;
}

function swatch(color: string): string {
  return `<span class="lp-swatch" style="background:${esc(color)}"></span>`;
}

function selected(): CurriculumTemplate | undefined {
  return templates.find(t => t.id === selectedId);
}

/** Entwurf aus der gespeicherten Vorlage, Fächer in ihrer Reihenfolge. */
function loadDraft(t: CurriculumTemplate | undefined) {
  const groupName = new Map((t?.groups ?? []).map(g => [g.id, g.name]));
  groups = (t?.groups ?? []).map(g => ({ name: g.name, pflichtUe: g.pflichtUe, color: g.color ?? null }));
  subjects = [...(t?.subjects ?? [])].sort((a, b) => a.sortOrder - b.sortOrder).map(x => ({
    name: x.name, shortName: x.shortName, color: x.color,
    group: x.groupId ? groupName.get(x.groupId) ?? null : null,
    pflichtUe: x.pflichtUe, schuleUe: x.schuleUe,
  }));
}

/** Entwurf speichern und mit der Antwort neu zeichnen. */
async function persist(): Promise<void> {
  const saved = await api.admin.curriculumTemplates.saveContent(selectedId!, {
    groups,
    subjects: subjects.map(x => ({ ...x, pflichtUe: x.group ? null : x.pflichtUe })),
  });
  templates = templates.map(t => (t.id === saved.id ? saved : t));
  loadDraft(saved);
  renderList();
  renderDetail();
}

/**
 * Oberste Ebene der Tafel: Fächer ohne Gruppe und Gruppen, jede Gruppe an der Stelle
 * ihres ersten Fachs. Gruppen ohne Fächer stehen am Ende.
 */
type Row = { type: "fach"; index: number } | { type: "gruppe"; name: string; members: number[] };
function rows(): Row[] {
  const out: Row[] = [];
  const placed = new Set<string>();
  subjects.forEach((x, index) => {
    if (!x.group) { out.push({ type: "fach", index }); return; }
    if (placed.has(x.group)) return;
    placed.add(x.group);
    out.push({ type: "gruppe", name: x.group, members: subjects.map((y, i) => (y.group === x.group ? i : -1)).filter(i => i >= 0) });
  });
  for (const g of groups) if (!placed.has(g.name)) out.push({ type: "gruppe", name: g.name, members: [] });
  return out;
}

// ── Darstellung ──

function renderList() {
  const box = currentEl?.querySelector<HTMLElement>("#vl-list");
  if (!box) return;
  if (templates.length === 0) {
    box.innerHTML = `<div class="vl-list-empty">Noch keine Vorlagen.</div>`;
    return;
  }
  box.innerHTML = templates.map(t => `
    <button type="button" class="vl-item${t.id === selectedId ? " active" : ""}" data-select="${t.id}">
      <span class="vl-item-title">${esc(t.name)}</span>
      <span class="vl-item-meta">${esc(t.departmentGroupName || "ohne Fachbereichsgruppe")} · ${t.subjects.length} Fächer</span>
      <span class="badge ${t.isPublic ? "badge-green" : "badge-gray"}">${t.isPublic ? "für Schulen sichtbar" : "Entwurf"}</span>
    </button>`).join("");
}

function renderDetail() {
  const box = currentEl?.querySelector<HTMLElement>("#vl-detail");
  if (!box) return;
  const t = selected();
  if (!t) {
    box.innerHTML = `<div class="card lp-empty">
      <h2>Keine Vorlage gewählt</h2>
      <p>Lege eine Vorlage an, etwa „Physiotherapie (PhysTh-APrV)“, und fülle sie per CSV oder Fach für Fach.</p>
    </div>`;
    return;
  }

  const fest = groups.reduce((a, g) => a + (g.pflichtUe ?? 0), 0)
    + subjects.filter(x => !x.group).reduce((a, x) => a + (x.pflichtUe ?? 0), 0);
  const list = rows();
  const body = list.map(r => {
    if (r.type === "fach") {
      const x = subjects[r.index];
      return `<tr class="lp-row" data-subject="${r.index}">
        <td>${swatch(x.color)}<span class="lp-name">${esc(x.name)}</span>${x.shortName ? `<span class="lp-short">${esc(x.shortName)}</span>` : ""}</td>
        <td class="lp-num">${fmt(x.pflichtUe)}</td>
        <td class="lp-num lp-muted">${fmt(x.schuleUe)}</td>
        <td class="lp-actions">${moveButtons(r.index)}</td>
      </tr>`;
    }
    const g = groups.find(x => x.name === r.name);
    const head = `<tr class="lp-group" data-group="${esc(r.name)}">
      <td>${g?.color ? swatch(g.color) : ""}<span class="lp-name">${esc(r.name)}</span><span class="lp-group-meta">${r.members.length} ${r.members.length === 1 ? "Fach" : "Fächer"}</span></td>
      <td class="lp-num">${fmt(g?.pflichtUe)}</td>
      <td class="lp-num lp-muted">${fmt(r.members.reduce((a, m) => a + (subjects[m].schuleUe ?? 0), 0) || null)}</td>
      <td class="lp-actions"></td>
    </tr>`;
    const members = r.members.map((m, j) => {
      const x = subjects[m];
      return `<tr class="lp-row lp-member${j === r.members.length - 1 ? " lp-member-last" : ""}" data-subject="${m}">
        <td>${swatch(x.color)}<span class="lp-name">${esc(x.name)}</span>${x.shortName ? `<span class="lp-short">${esc(x.shortName)}</span>` : ""}</td>
        <td class="lp-num"></td>
        <td class="lp-num lp-muted">${fmt(x.schuleUe)}</td>
        <td class="lp-actions">${moveButtons(m)}</td>
      </tr>`;
    }).join("");
    return head + (r.members.length ? members
      : `<tr class="lp-member lp-member-empty" data-group="${esc(r.name)}"><td colspan="4">Noch keine Fächer in dieser Gruppe.</td></tr>`);
  }).join("");

  box.innerHTML = `
    <div class="card vl-head">
      <div class="vl-head-main">
        <h2>${esc(t.name)}</h2>
        <div class="vl-head-meta">
          <span>${esc(t.departmentGroupName || "Ohne Fachbereichsgruppe")}</span>
          <span>Gesamt laut Verordnung: ${t.totalUe !== null ? `${esc(fmt(t.totalUe))} UE` : "nicht hinterlegt"}</span>
          <span>Pflicht laut Verordnung: ${esc(fmt(fest))} UE</span>
          ${t.poolUe !== null ? `<span>Pool: ${esc(fmt(t.poolUe))} UE</span>` : ""}
          <span class="badge ${t.isPublic ? "badge-green" : "badge-gray"}">${t.isPublic ? "für Schulen sichtbar" : "Entwurf"}</span>
        </div>
      </div>
      <div class="vl-head-actions">
        <button class="btn btn-secondary btn-sm" data-action="head">Bearbeiten</button>
        <button class="btn btn-secondary btn-sm" data-action="csv">CSV-Import</button>
      </div>
    </div>

    <div class="vl-toolbar">
      <h3>Stundentafel</h3>
      <div class="settings-page-actions">
        <button class="btn btn-secondary btn-sm" data-action="group">+ Fächergruppe</button>
        <button class="btn btn-primary btn-sm" data-action="subject">+ Fach</button>
      </div>
    </div>
    <div class="card table-wrap lp-card">
      <table class="lp-table">
        <thead><tr>
          <th>Fach</th>
          <th class="lp-num" style="width:130px">Pflicht</th>
          <th class="lp-num" style="width:160px" title="Startwert für „UE Schule“ beim Übernehmen, etwa die Aufteilung einer Gruppenpflicht">Vorschlag Schule</th>
          <th class="lp-actions"></th>
        </tr></thead>
        <tbody>${body || `<tr><td colspan="4" class="empty-state">Noch leer. Lege Fächer an oder importiere eine CSV.</td></tr>`}</tbody>
      </table>
    </div>`;
}

function moveButtons(index: number): string {
  // Verschoben wird innerhalb derselben Ebene: Fächer ohne Gruppe untereinander, Fächer
  // einer Gruppe innerhalb der Gruppe.
  const peers = subjects.map((x, i) => (x.group === subjects[index].group ? i : -1)).filter(i => i >= 0);
  const pos = peers.indexOf(index);
  return `<div class="lp-move">
    <button type="button" class="btn-icon" data-move="${index}" data-dir="-1" title="Nach oben" aria-label="Nach oben"${pos === 0 ? " disabled" : ""}>${Icons.chevronUp}</button>
    <button type="button" class="btn-icon" data-move="${index}" data-dir="1" title="Nach unten" aria-label="Nach unten"${pos === peers.length - 1 ? " disabled" : ""}>${Icons.chevronDown}</button>
  </div>`;
}

// ── Fenster ──

function actions(dangerLabel?: string, saveLabel = "Speichern"): string {
  return `
    <div class="alert alert-error hidden"></div>
    <div class="form-actions">
      ${dangerLabel ? `<button type="button" class="lp-danger-link" id="m-danger">${esc(dangerLabel)}</button>` : ""}
      <button type="button" class="btn btn-secondary" id="m-cancel">Abbrechen</button>
      <button type="button" class="btn btn-primary" id="m-save">${esc(saveLabel)}</button>
    </div>`;
}

function showError(mb: HTMLElement, message: string) {
  const box = mb.querySelector(".alert-error") as HTMLElement;
  box.textContent = message;
  box.classList.remove("hidden");
}

/** Speichern mit Fehleranzeige im Fenster; schließt es bei Erfolg. */
async function saveFrom(mb: HTMLElement, fn: () => Promise<void>, done: string) {
  const btn = mb.querySelector("#m-save") as HTMLButtonElement;
  btn.disabled = true;
  try {
    await fn();
    closeModal();
    showToast(done);
  } catch (err: any) {
    showError(mb, err.message);
    btn.disabled = false;
  }
}

function openHeadForm(t?: CurriculumTemplate) {
  const isEdit = !!t;
  const groupOpts = `<option value="">Keine Fachbereichsgruppe</option>` + groupsCatalog
    .map(g => `<option value="${g.id}" ${t?.departmentGroupId === +g.id ? "selected" : ""}>${esc(g.name)}</option>`).join("");
  openModal(isEdit ? "Vorlage bearbeiten" : "Neue Lehrplan-Vorlage", `<div class="lp-form">
    <div class="form-group"><label for="vh-name">Name *</label><input id="vh-name" value="${esc(t?.name || "")}" placeholder="z.B. Physiotherapie (PhysTh-APrV)" /></div>
    <div class="form-grid">
      <div class="form-group">
        <label for="vh-group">Fachbereichsgruppe</label>
        <select id="vh-group">${groupOpts}</select>
        <small class="text-muted">Schulen mit Klassen dieser Gruppe bekommen die Vorlage als passend angezeigt.</small>
      </div>
      <div class="form-group">
        <label for="vh-total">Gesamt laut Verordnung</label>
        ${ueInput("vh-total", t?.totalUe)}
      </div>
    </div>
    <div class="form-grid">
      <div class="form-group">
        <label for="vh-pool">Pool-Stunden</label>
        ${ueInput("vh-pool", t?.poolUe)}
      </div>
      <small class="text-muted vl-pool-hint">Stunden, die die Schule selbst auf Fächer verteilt. Beim Übernehmen wird sie darauf hingewiesen und vergibt sie im Lehrplan.</small>
    </div>
    <label class="checkbox-label" style="margin-top:.25rem">
      <input type="checkbox" id="vh-public" ${t?.isPublic ? "checked" : ""} />
      <span><span style="font-weight:500">Für Schulen sichtbar</span>
        <span style="display:block;font-size:.8125rem;color:var(--gray-500)">Solange der Haken fehlt, ist die Vorlage ein Entwurf und nur hier zu sehen.</span></span>
    </label></div>
    ${actions(isEdit ? "Vorlage löschen" : undefined, isEdit ? "Speichern" : "Anlegen")}`);

  const mb = modalBody();
  mb.querySelector("#m-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#m-danger")?.addEventListener("click", async () => {
    const ok = await confirmDialog({
      title: "Vorlage löschen",
      message: `„${t!.name}“ wird gelöscht. Schulen, die sie übernommen haben, behalten ihren Lehrplan.`,
      confirmLabel: "Löschen", danger: true,
    });
    if (!ok) return;
    try {
      await api.admin.curriculumTemplates.delete(t!.id);
      templates = templates.filter(x => x.id !== t!.id);
      selectedId = templates[0]?.id ?? null;
      loadDraft(selected());
      closeModal();
      renderList(); renderDetail();
      showToast("Vorlage gelöscht");
    } catch (err: any) { showError(mb, err.message); }
  });
  mb.querySelector("#m-save")!.addEventListener("click", () => {
    const total = parseUe((mb.querySelector("#vh-total") as HTMLInputElement).value);
    const pool = parseUe((mb.querySelector("#vh-pool") as HTMLInputElement).value);
    const name = (mb.querySelector("#vh-name") as HTMLInputElement).value.trim();
    if (!name) { showError(mb, "Bitte einen Namen eingeben"); return; }
    if (total === "invalid") { showError(mb, "Gesamt muss eine Zahl ab 0 sein"); return; }
    if (pool === "invalid") { showError(mb, "Pool-Stunden müssen eine Zahl ab 0 sein"); return; }
    const data = {
      name,
      departmentGroupId: +(mb.querySelector("#vh-group") as HTMLSelectElement).value || null,
      totalUe: total,
      poolUe: pool,
      isPublic: (mb.querySelector("#vh-public") as HTMLInputElement).checked,
    };
    saveFrom(mb, async () => {
      const saved = isEdit
        ? await api.admin.curriculumTemplates.update(t!.id, data)
        : await api.admin.curriculumTemplates.create(data);
      templates = await api.admin.curriculumTemplates.list();
      selectedId = saved.id;
      loadDraft(selected());
      renderList(); renderDetail();
    }, isEdit ? "Vorlage gespeichert" : "Vorlage angelegt");
  });
  (mb.querySelector("#vh-name") as HTMLInputElement).focus();
}

function openSubjectForm(index?: number) {
  const isEdit = index !== undefined;
  const x = isEdit ? subjects[index!] : null;
  const groupOpts = `<option value="">Keine Gruppe</option>` + groups
    .map(g => `<option value="${esc(g.name)}" ${g.name === x?.group ? "selected" : ""}>${esc(g.name)}${g.pflichtUe !== null ? ` (Pflicht ${fmt(g.pflichtUe)} UE)` : ""}</option>`).join("");

  openModal(isEdit ? "Fach bearbeiten" : "Neues Fach", `<div class="lp-form">
    <div class="lp-new-grid">
      <div class="form-group"><label for="vs-name">Bezeichnung *</label><input id="vs-name" value="${esc(x?.name || "")}" placeholder="z.B. Anatomie (Organe)" /></div>
      <div class="form-group"><label for="vs-short">Kürzel</label><input id="vs-short" value="${esc(x?.shortName || "")}" /></div>
      <div class="form-group"><label for="vs-color">Farbe</label><input type="color" id="vs-color" value="${esc(x?.color || "#2563eb")}" /></div>
    </div>
    <div class="form-group">
      <label for="vs-group">Fächergruppe</label>
      <select id="vs-group">${groupOpts}</select>
    </div>
    <div class="form-grid">
      <div class="form-group" id="vs-pflicht-field">
        <label for="vs-pflicht">Pflicht laut Verordnung</label>
        ${ueInput("vs-pflicht", x?.pflichtUe)}
        <small class="text-muted">Nur, wenn die Verordnung das Fach einzeln vorgibt.</small>
      </div>
      <div class="form-group">
        <label for="vs-schule">Vorschlag Schule</label>
        ${ueInput("vs-schule", x?.schuleUe)}
        <small class="text-muted">Startwert für „UE Schule“, wenn eine Schule die Vorlage übernimmt.</small>
      </div>
    </div>
    <div class="lp-over-note hidden" id="vs-over"></div></div>
    ${actions(isEdit ? "Fach entfernen" : undefined)}`);

  const mb = modalBody();
  const groupSelect = mb.querySelector("#vs-group") as HTMLSelectElement;
  const colorInput = mb.querySelector("#vs-color") as HTMLInputElement;
  const pflicht = mb.querySelector("#vs-pflicht") as HTMLInputElement;
  let own = pflicht.value;
  pflicht.addEventListener("input", () => { own = pflicht.value; });
  const schuleInput = mb.querySelector("#vs-schule") as HTMLInputElement;
  const sync = () => {
    const g = groups.find(y => y.name === groupSelect.value);
    mb.querySelector("#vs-pflicht-field")!.classList.toggle("hidden", !!g);
    const own = parseUe(schuleInput.value);
    const others = subjects.filter((y, i) => y.group === g?.name && i !== index).reduce((a, y) => a + (y.schuleUe ?? 0), 0);
    mb.querySelector("#vs-over")!.outerHTML = overNote("vs-over", g?.name, g?.pflichtUe ?? null,
      others + (typeof own === "number" ? own : 0), "kommt mit den Vorschlägen auf");
  };
  schuleInput.addEventListener("input", sync);
  groupSelect.addEventListener("change", sync);
  sync();
  // Hat die Gruppe eine gemeinsame Farbe, übernimmt das Fach sie beim Zuordnen. Danach
  // bleibt die Farbe am Fach frei wählbar.
  const takeGroupColor = () => {
    const color = groups.find(y => y.name === groupSelect.value)?.color;
    if (color) colorInput.value = color;
  };
  groupSelect.addEventListener("change", takeGroupColor);

  mb.querySelector("#m-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#m-danger")?.addEventListener("click", () => {
    const before = [...subjects];
    subjects.splice(index!, 1);
    saveFrom(mb, async () => {
      try { await persist(); } catch (e) { subjects = before; throw e; }
    }, "Fach entfernt");
  });
  mb.querySelector("#m-save")!.addEventListener("click", () => {
    const name = (mb.querySelector("#vs-name") as HTMLInputElement).value.trim();
    const p = parseUe(own), sch = parseUe((mb.querySelector("#vs-schule") as HTMLInputElement).value);
    if (!name) { showError(mb, "Bitte eine Bezeichnung eingeben"); return; }
    if (p === "invalid" || sch === "invalid") { showError(mb, "UE müssen Zahlen ab 0 sein"); return; }
    if (subjects.some((y, i) => i !== index && y.name.trim().toLowerCase() === name.toLowerCase())) {
      showError(mb, `„${name}“ steht schon in der Vorlage`); return;
    }
    const next: DraftSubject = {
      name, shortName: (mb.querySelector("#vs-short") as HTMLInputElement).value.trim() || null,
      color: (mb.querySelector("#vs-color") as HTMLInputElement).value,
      group: groupSelect.value || null, pflichtUe: groupSelect.value ? null : p, schuleUe: sch,
    };
    const before = [...subjects];
    if (isEdit) subjects[index!] = next; else subjects.push(next);
    saveFrom(mb, async () => {
      try { await persist(); } catch (e) { subjects = before; throw e; }
    }, isEdit ? "Fach gespeichert" : "Fach angelegt");
  });
  (mb.querySelector("#vs-name") as HTMLInputElement).focus();
}

function openGroupForm(name?: string) {
  const isEdit = name !== undefined;
  const g = isEdit ? groups.find(x => x.name === name)! : null;
  const firstColor = isEdit ? subjects.find(x => x.group === name)?.color : undefined;
  openModal(isEdit ? "Fächergruppe bearbeiten" : "Neue Fächergruppe", `<div class="lp-form">
    <p class="lp-modal-lead">Für Fächer, deren Pflicht die Verordnung gemeinsam vorgibt. Die Fächer ordnest du am Fach der Gruppe zu.</p>
    <div class="lp-group-grid">
      <div class="form-group"><label for="vg-name">Bezeichnung *</label><input id="vg-name" value="${esc(g?.name || "")}" placeholder="z.B. Anatomie" /></div>
      <div class="form-group"><label for="vg-pflicht">Pflicht laut Verordnung</label>${ueInput("vg-pflicht", g?.pflichtUe)}</div>
    </div>
    <div class="vl-group-color">
      <label class="checkbox-label">
        <input type="checkbox" id="vg-color-on" ${g?.color ? "checked" : ""} />
        <span><span style="font-weight:500">Einheitliche Farbe für alle Fächer der Gruppe</span>
          <span style="display:block;font-size:.8125rem;color:var(--gray-500)">Beim Speichern bekommen alle Fächer dieser Gruppe die Farbe, neu zugeordnete Fächer ebenso.</span></span>
      </label>
      <input type="color" id="vg-color" value="${esc(g?.color || firstColor || "#2563eb")}" ${g?.color ? "" : "disabled"} aria-label="Farbe der Gruppe" />
    </div></div>
    ${actions(isEdit ? "Gruppe löschen" : undefined)}`);
  const mb = modalBody();
  const colorOn = mb.querySelector("#vg-color-on") as HTMLInputElement;
  const colorInput = mb.querySelector("#vg-color") as HTMLInputElement;
  colorOn.addEventListener("change", () => { colorInput.disabled = !colorOn.checked; });
  mb.querySelector("#m-cancel")!.addEventListener("click", closeModal);
  mb.querySelector("#m-danger")?.addEventListener("click", () => {
    const beforeG = [...groups], beforeS = subjects.map(x => ({ ...x }));
    groups = groups.filter(x => x.name !== name);
    subjects = subjects.map(x => (x.group === name ? { ...x, group: null } : x));
    saveFrom(mb, async () => {
      try { await persist(); } catch (e) { groups = beforeG; subjects = beforeS; throw e; }
    }, "Gruppe gelöscht, ihre Fächer stehen jetzt einzeln");
  });
  mb.querySelector("#m-save")!.addEventListener("click", () => {
    const newName = (mb.querySelector("#vg-name") as HTMLInputElement).value.trim();
    const p = parseUe((mb.querySelector("#vg-pflicht") as HTMLInputElement).value);
    if (!newName) { showError(mb, "Bitte eine Bezeichnung eingeben"); return; }
    if (p === "invalid") { showError(mb, "Die Pflicht muss eine Zahl ab 0 sein"); return; }
    if (groups.some(x => x.name !== name && x.name.toLowerCase() === newName.toLowerCase())) {
      showError(mb, `Die Gruppe „${newName}“ gibt es schon`); return;
    }
    const color = colorOn.checked ? colorInput.value : null;
    const beforeG = [...groups], beforeS = subjects.map(x => ({ ...x }));
    if (isEdit) {
      groups = groups.map(x => (x.name === name ? { name: newName, pflichtUe: p, color } : x));
      subjects = subjects.map(x => (x.group === name ? { ...x, group: newName, color: color ?? x.color } : x));
    } else {
      groups.push({ name: newName, pflichtUe: p, color });
    }
    saveFrom(mb, async () => {
      try { await persist(); } catch (e) { groups = beforeG; subjects = beforeS; throw e; }
    }, "Fächergruppe gespeichert");
  });
  (mb.querySelector("#vg-name") as HTMLInputElement).focus();
}

async function move(index: number, dir: number) {
  const peers = subjects.map((x, i) => (x.group === subjects[index].group ? i : -1)).filter(i => i >= 0);
  const pos = peers.indexOf(index);
  const other = peers[pos + dir];
  if (other === undefined) return;
  [subjects[index], subjects[other]] = [subjects[other], subjects[index]];
  try { await persist(); } catch (err: any) { showToast("Fehler: " + err.message); }
}

// ── Übertragen zwischen Systemen ──
//
// Vorlagen werden auf dem Testsystem gepflegt und per JSON-Datei auf Produktion
// übertragen. Der Server ersetzt Vorlagen gleichen Namens (sie behalten ihre
// Sichtbarkeit) und legt neue als Entwurf an.

function templateMeta(departmentGroupName: string | null, subjectCount: number): string {
  return `${esc(departmentGroupName || "ohne Fachbereichsgruppe")} · ${subjectCount} ${subjectCount === 1 ? "Fach" : "Fächer"}`;
}

function openExportForm() {
  if (templates.length === 0) { showToast("Noch keine Vorlagen zum Exportieren"); return; }
  openModal("Vorlagen exportieren", `<div class="lp-form">
    <p class="lp-modal-lead">Die Datei enthält Kopfdaten, Fächergruppen und Fächer der gewählten Vorlagen. Auf dem anderen System lädst du sie über „Importieren“ hoch.</p>
    <label class="lp-check vl-check-all"><input type="checkbox" id="ve-all" checked /><span class="lp-check-name">Alle auswählen</span></label>
    <div class="lp-checklist">
      ${templates.map(t => `<label class="lp-check">
        <input type="checkbox" value="${t.id}" checked />
        <span class="lp-check-name">${esc(t.name)}<span class="vl-check-meta">${templateMeta(t.departmentGroupName, t.subjects.length)}</span></span>
        <span class="badge ${t.isPublic ? "badge-green" : "badge-gray"}">${t.isPublic ? "für Schulen sichtbar" : "Entwurf"}</span>
      </label>`).join("")}
    </div></div>
    ${actions(undefined, "Exportieren")}`, { wide: true, stickyActions: true });

  const mb = modalBody();
  const all = mb.querySelector("#ve-all") as HTMLInputElement;
  const boxes = () => Array.from(mb.querySelectorAll<HTMLInputElement>(".lp-checklist input"));
  const save = mb.querySelector("#m-save") as HTMLButtonElement;
  const sync = () => {
    const n = boxes().filter(b => b.checked).length;
    all.checked = n === templates.length;
    all.indeterminate = n > 0 && n < templates.length;
    save.textContent = n ? `Exportieren (${n})` : "Exportieren";
    save.disabled = n === 0;
  };
  all.addEventListener("change", () => { boxes().forEach(b => { b.checked = all.checked; }); sync(); });
  mb.querySelector(".lp-checklist")!.addEventListener("change", sync);
  sync();

  mb.querySelector("#m-cancel")!.addEventListener("click", closeModal);
  save.addEventListener("click", () => {
    const ids = boxes().filter(b => b.checked).map(b => +b.value);
    saveFrom(mb, () => api.admin.curriculumTemplates.exportFile(ids),
      ids.length === 1 ? "Vorlage exportiert" : `${ids.length} Vorlagen exportiert`);
  });
}

/** Grobe Prüfung im Browser, damit die Vorschau stimmt. Die eigentliche Prüfung macht der Server. */
function readTransferFile(text: string): { name: string; departmentGroupName: string | null; subjectCount: number }[] | string {
  let data: any;
  try { data = JSON.parse(text); } catch { return "Die Datei ist keine gültige JSON-Datei"; }
  if (data?.format !== "meduvio-lehrplan-vorlagen" || !Array.isArray(data?.templates)) {
    return "Die Datei ist kein Export von Lehrplan-Vorlagen";
  }
  if (data.version !== 1) return "Diese Exportversion wird nicht unterstützt";
  if (data.templates.length === 0) return "Die Datei enthält keine Vorlagen";
  return data.templates.map((t: any) => ({
    name: String(t?.name ?? "").trim(),
    departmentGroupName: t?.departmentGroupName ? String(t.departmentGroupName) : null,
    subjectCount: Array.isArray(t?.subjects) ? t.subjects.length : 0,
  }));
}

function openImportForm() {
  openModal("Vorlagen importieren", `<div class="lp-form">
    <p class="lp-modal-lead">Lade eine Datei hoch, die du auf einem anderen System über „Exportieren“ erzeugt hast.
      Vorhandene Vorlagen mit gleichem Namen werden mit Kopfdaten und Inhalt ersetzt und behalten ihre Sichtbarkeit.
      Neue Vorlagen kommen als Entwurf an. Schulen, die eine Vorlage übernommen haben, behalten ihren Lehrplan.</p>
    <div class="form-group">
      <label for="vi-file">Exportdatei (.json)</label>
      <input type="file" id="vi-file" accept=".json,application/json" />
    </div>
    <div id="vi-preview"></div></div>
    ${actions(undefined, "Importieren")}`, { wide: true, stickyActions: true });

  const mb = modalBody();
  const save = mb.querySelector("#m-save") as HTMLButtonElement;
  const preview = mb.querySelector("#vi-preview") as HTMLElement;
  const errorBox = mb.querySelector(".alert-error") as HTMLElement;
  let data: unknown = null;
  // Wird schnell nacheinander eine zweite Datei gewählt, darf die erste sie nicht überholen.
  let run = 0;
  save.disabled = true;

  mb.querySelector("#vi-file")!.addEventListener("change", async (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    const current = ++run;
    data = null;
    save.disabled = true;
    preview.innerHTML = "";
    errorBox.classList.add("hidden");
    if (!file) return;
    const text = await file.text();
    if (current !== run) return;
    const items = readTransferFile(text);
    if (typeof items === "string") { showError(mb, items); return; }
    data = JSON.parse(text);
    const existing = new Set(templates.map(t => t.name.trim().toLowerCase()));
    const replaced = items.filter(i => existing.has(i.name.toLowerCase())).length;
    preview.innerHTML = `
      <div class="lp-checklist">
        ${items.map(i => `<div class="lp-check">
          <span class="lp-check-name">${esc(i.name || "ohne Namen")}<span class="vl-check-meta">${templateMeta(i.departmentGroupName, i.subjectCount)}</span></span>
          ${existing.has(i.name.toLowerCase())
            ? `<span class="badge badge-amber">ersetzt vorhandene</span>`
            : `<span class="badge badge-gray">neu, als Entwurf</span>`}
        </div>`).join("")}
      </div>
      <div class="lp-check-sum">${items.length - replaced} neu, ${replaced} ${replaced === 1 ? "wird" : "werden"} ersetzt</div>`;
    save.disabled = false;
  });

  const cancel = mb.querySelector("#m-cancel") as HTMLButtonElement;
  cancel.addEventListener("click", closeModal);
  save.addEventListener("click", async () => {
    if (!data) return;
    save.disabled = true;
    errorBox.classList.add("hidden");
    try {
      const result = await api.admin.curriculumTemplates.importData(data);
      templates = await api.admin.curriculumTemplates.list();
      if (!selected()) selectedId = templates[0]?.id ?? null;
      loadDraft(selected());
      renderList(); renderDetail();
      const done = `${result.created} ${result.created === 1 ? "Vorlage" : "Vorlagen"} angelegt, ${result.updated} aktualisiert`;
      if (result.warnings.length === 0) { closeModal(); showToast(done); return; }
      // Hinweise bleiben im Fenster stehen, ein Toast wäre zu schnell wieder weg.
      preview.innerHTML = `<div class="alert alert-success">${esc(done)}</div>
        <div class="alert alert-warning"><ul class="vl-warnings">${result.warnings.map(w => `<li>${esc(w)}</li>`).join("")}</ul></div>`;
      (mb.querySelector("#vi-file") as HTMLInputElement).closest(".form-group")!.remove();
      save.remove();
      cancel.textContent = "Schließen";
    } catch (err: any) {
      showError(mb, err.message);
      save.disabled = false;
    }
  });
}

// ── Seite ──

export async function render(el: HTMLElement) {
  el.innerHTML = `
    <div class="page-header">
      <div>
        <h1>Lehrplan-Vorlagen</h1>
        <p class="text-muted" style="margin:.25rem 0 0;font-size:.875rem">Stundentafeln der Verordnungen je Beruf. Schulen übernehmen sie unter Einstellungen, Stundenplan, Lehrplan und passen sie danach selbst an.</p>
      </div>
      <div class="vl-page-actions">
        <button class="btn btn-secondary" id="vl-import">Importieren</button>
        <button class="btn btn-secondary" id="vl-export">Exportieren</button>
        <button class="btn btn-primary" id="vl-new">+ Neue Vorlage</button>
      </div>
    </div>
    <div class="vl-layout">
      <div class="card vl-list" id="vl-list"></div>
      <div id="vl-detail"></div>
    </div>`;
  currentEl = el.querySelector(".vl-layout") as HTMLElement;

  try {
    [templates, groupsCatalog] = await Promise.all([
      api.admin.curriculumTemplates.list(),
      api.admin.departmentGroups.list(),
    ]);
  } catch (err: any) {
    el.innerHTML = `<div class="alert alert-error">Fehler: ${esc(err.message)}</div>`;
    return;
  }
  selectedId = templates[0]?.id ?? null;
  loadDraft(selected());
  renderList();
  renderDetail();

  el.querySelector("#vl-new")!.addEventListener("click", () => openHeadForm());
  el.querySelector("#vl-export")!.addEventListener("click", openExportForm);
  el.querySelector("#vl-import")!.addEventListener("click", openImportForm);

  currentEl.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const item = target.closest("[data-select]") as HTMLElement | null;
    if (item) {
      selectedId = +item.dataset.select!;
      loadDraft(selected());
      renderList(); renderDetail();
      return;
    }
    const t = selected();
    if (!t) return;
    const action = (target.closest("[data-action]") as HTMLElement | null)?.dataset.action;
    if (action === "head") { openHeadForm(t); return; }
    if (action === "group") { openGroupForm(); return; }
    if (action === "subject") { openSubjectForm(); return; }
    if (action === "csv") {
      openCsvImportModal({
        title: `CSV-Import: ${t.name}`,
        duplicateHint: "Der Import ersetzt den gesamten Inhalt dieser Vorlage. Name, Fachbereichsgruppe und Gesamtumfang bleiben.",
        fieldDescriptions: [
          { field: "Fächergruppe", hint: "Optional. Eine Zeile nur mit Gruppe und UE Verordnung setzt die Pflicht der Gruppe" },
          { field: "Fach", hint: "Pflichtfeld, außer in einer reinen Gruppenzeile" },
          { field: "Kürzel", hint: "Optional" },
          { field: "Farbe", hint: "Optional, als Hexwert wie #2563eb" },
          { field: "UE Schule", hint: "Optional, Vorschlag für die Schule" },
          { field: "UE Verordnung", hint: "Optional. Mit Gruppe gilt der Wert für die Gruppe, ohne Gruppe für das Fach" },
        ],
        onDownloadTemplate: () => api.admin.curriculumTemplates.csvTemplate(),
        onImport: (file) => api.admin.curriculumTemplates.csvImport(t.id, file),
        onComplete: async () => {
          templates = await api.admin.curriculumTemplates.list();
          loadDraft(selected());
          renderList(); renderDetail();
        },
      });
      return;
    }
    const mv = target.closest("[data-move]") as HTMLElement | null;
    if (mv) { move(+mv.dataset.move!, +mv.dataset.dir!); return; }
    const groupRow = target.closest("[data-group]") as HTMLElement | null;
    if (groupRow) { openGroupForm(groupRow.dataset.group!); return; }
    const subjectRow = target.closest("[data-subject]") as HTMLElement | null;
    if (subjectRow) openSubjectForm(+subjectRow.dataset.subject!);
  });
}
